import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Paper,
  Typography, 
  CircularProgress, 
  Alert,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { api } from '@/services';
import type { DashboardData } from '@/types/models';
import { ROUTES } from '@/config/routes';
import { Theme } from '@mui/material/styles';

type MatchedOpportunity = DashboardData['matched_opportunities'][number];

const getStatusColor = (status: string): "success" | "warning" | "error" | "info" => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'info';
  }
};

const getMatchScoreColor = (score: number): "success" | "warning" | "error" | "info" => {
  const percentage = score * 100;
  if (percentage >= 70) return "success";
  if (percentage >= 40) return "warning";
  return "error";
};

const MatchScoreChip: React.FC<{ score: number }> = ({ score }) => {
  const percentage = Math.round(score * 100);
  const color = getMatchScoreColor(score);
  
  return (
    <Chip
      label={`${percentage}% Match`}
      color={color}
      size="small"
      sx={{ 
        fontWeight: 'medium',
        minWidth: 90,
        '& .MuiChip-label': {
          px: 1
        }
      }}
    />
  );
};

const OpportunityCard: React.FC<{ match: MatchedOpportunity }> = ({ match }) => {
  const { opportunity } = match;
  
  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      }
    }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 'bold'
            }}
          >
            {opportunity.title}
          </Typography>
          <MatchScoreChip score={match.match_score} />
        </Box>
        
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {opportunity.organization.name}
              {opportunity.organization.industry && ` • ${opportunity.organization.industry}`}
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {opportunity.commitment_hours} hours
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Required Skills
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {opportunity.required_skills.map((skill: string) => (
              <Chip
                key={skill}
                label={skill}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
      <Box sx={{ p: 2, pt: 2 }}>
        <Button
          component={Link}
          to={ROUTES.STUDENT.OPPORTUNITIES.DETAIL(opportunity.id)}
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardIcon />}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        const response = await api.dashboard.getStudentDashboard();
        console.log('Dashboard API Response:', response);
        
        const data = response.data;
        
        // Validate the response data
        if (!data?.profile || !data?.hours || !data?.applications || !data?.matched_opportunities) {
          console.error('Invalid response structure:', data);
          throw new Error('Invalid data received from the server');
        }

        setDashboardData(data);
      } catch (error) {
        console.error('Dashboard loading error:', error);
        setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  useEffect(() => {
    console.log('Current dashboard data:', dashboardData);
  }, [dashboardData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    console.log('No dashboard data available');
    return (
      <Box m={2}>
        <Alert severity="warning">
          No dashboard data available. Please try refreshing the page.
        </Alert>
      </Box>
    );
  }

  const handleHoursClick = () => navigate(ROUTES.STUDENT.LOG_HOURS);
  const handleApplicationsClick = () => navigate(ROUTES.STUDENT.APPLICATIONS.LIST);

  const handleApplicationClick = (applicationId: number) => {
    navigate(ROUTES.STUDENT.APPLICATIONS.DETAIL(applicationId));
  };

  return (
    <>
      {/* Welcome Section */}
      <Box mb={6}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          Welcome Back! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your volunteer journey and discover new opportunities.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Progress Overview */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              borderRadius: 4
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Volunteer Progress
            </Typography>
            <Box sx={{ my: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {Math.round(dashboardData.hours.total_approved)} hrs
                </Typography>
                <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  40 hrs
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((dashboardData.hours.total_approved / 40) * 100, 100)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white',
                    borderRadius: 5,
                  }
                }}
              />
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={handleHoursClick}
              sx={{ 
                mt: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
              startIcon={<AddIcon />}
            >
              Log Hours
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {/* Recent Applications */}
          <Card sx={{ 
            borderRadius: 4,
            height: '100%'
          }}>
            <CardContent sx={{ 
              p: 2,
              pt: 1.5,
              pb: 1.5,
              height: '100%'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">Recent Applications</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleApplicationsClick}
                  sx={{ borderRadius: 2 }}
                >
                  View All
                </Button>
              </Box>
              
              <Stack spacing={1}>
                {dashboardData.applications.slice(0, 3).map((app) => (
                  <Paper
                    key={app.id}
                    elevation={0}
                    onClick={() => handleApplicationClick(app.id)}
                    sx={{ 
                      p: 1,
                      bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      position: 'relative',
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.background.default, 0.9),
                        transform: 'translateX(8px)'
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 4,
                        height: '60%',
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: (theme: Theme) => {
                          const status = app.status.toLowerCase();
                          const colorKey = getStatusColor(status);
                          return theme.palette[colorKey].main;
                        }
                      }
                    }}
                  >
                    <Box sx={{ pl: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            {app.opportunity.title}
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {app.opportunity.organization}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                bgcolor: 'background.paper',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                color: 'text.secondary'
                              }}
                            >
                              Applied on {new Date(app.created_at).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Box>
                        <Chip
                          label={app.status}
                          color={getStatusColor(app.status)}
                          size="small"
                          sx={{ 
                            minWidth: 90, 
                            textAlign: 'center',
                            textTransform: 'capitalize',
                            fontWeight: 'medium'
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Matched Opportunities */}
      <Box mt={4}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Recommended Opportunities
          </Typography>
          <Chip
            label={`${dashboardData.matched_opportunities.length} matches`}
            color="primary"
            size="small"
            sx={{ 
              borderRadius: 2,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 'medium'
            }}
          />
        </Box>
        <Grid container spacing={3}>
          {dashboardData.matched_opportunities.map((match) => (
            <Grid item xs={12} sm={6} md={4} key={match.opportunity.id}>
              <OpportunityCard match={match} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
};

export default Dashboard;
